import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { Icon, Label } from '@atlas/ui';

const scrollToBottom = element => {
  if (!element) return;

  element.scrollTop = element.scrollHeight;
  element.scrollTo?.({ top: element.scrollHeight, behavior: 'auto' });
};

const PixDepositResultQRCard = ({
  formattedAmount,
  hasCopyButton,
  isCopied,
  isExpired,
  onCopy,
  pixCode,
}) => {
  const [isQRVisible, setIsQRVisible] = useState(false);
  const cardRef = useRef(null);
  const qrRef = useRef(null);

  const handleToggleQR = () => setIsQRVisible(prev => !prev);

  useEffect(() => {
    if (!isQRVisible) return;

    const scrollableForm = qrRef.current?.closest('.pix-deposit-result__form');

    scrollToBottom(cardRef.current);
    scrollToBottom(scrollableForm);
  }, [isQRVisible]);

  const isCopiedAndActive = isCopied && !isExpired;
  const copyLabelMessage = isCopiedAndActive ? 'copied_pix_code' : 'copy_pix_code';

  const qrCodeClassName = cx({ 'pix-deposit-result__card-qr-expired': isExpired });
  const copyBtnClassName = cx('pix-deposit-result__card-copy-btn', {
    'pix-deposit-result__card-copy-btn-active': !isCopiedAndActive && !isExpired,
    'pix-deposit-result__card-copy-btn-copied': isCopiedAndActive,
    'pix-deposit-result__card-copy-btn-expired': isExpired,
  });

  return (
    <div className="pix-deposit-result__card-container">
      <div className="pix-deposit-result__card" ref={cardRef}>
        <div className="pix-deposit-result__card-info">
          <div className="pix-deposit-result__card-info-left">
            <div className="pix-deposit-result__card-amount">
              <Label className="pix-deposit-result__card-amount-label" message="deposit" />
              <span className="pix-deposit-result__card-amount-value">{formattedAmount}</span>
            </div>
            <div className="pix-deposit-result__card-code-wrapper">
              <span className="pix-deposit-result__card-code">{pixCode}</span>
              <button
                className="pix-deposit-result__card-code-btn"
                onClick={handleToggleQR}
                type="button"
              >
                <Icon className="pix-deposit-result__card-code-icon" name="qr-code" />
              </button>
            </div>
          </div>
        </div>
        {hasCopyButton && (
          <button
            className={copyBtnClassName}
            disabled={isCopied || isExpired}
            onClick={onCopy}
            type="button"
          >
            {isCopiedAndActive && (
              <Icon className="pix-deposit-result__card-copy-btn-icon" name="done" />
            )}
            <Label message={copyLabelMessage} />
          </button>
        )}
        {isQRVisible && (
          <div className="pix-deposit-result__card-qr-wrapper" ref={qrRef}>
            <QRCode className={qrCodeClassName} size={98} value={pixCode || ''} />
            {isExpired && (
              <>
                <div className="pix-deposit-result__card-qr-overlay" />
                <Icon className="pix-deposit-result__card-qr-lock" name="lock" />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

PixDepositResultQRCard.propTypes = {
  formattedAmount: PropTypes.string.isRequired,
  hasCopyButton: PropTypes.bool,
  isCopied: PropTypes.bool.isRequired,
  isExpired: PropTypes.bool.isRequired,
  onCopy: PropTypes.func,
  pixCode: PropTypes.string,
};

export default PixDepositResultQRCard;
